import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HeartHandshake, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import HouseholdNav from "@/components/HouseholdNav";

function fmtINR(v) {
    const n = Number(v || 0);
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000)   return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
}

export default function CoupleCheckIn() {
    const monthDefault = new Date().toISOString().slice(0, 7);
    const [month, setMonth] = useState(monthDefault);
    const [data, setData]   = useState(null);
    useEffect(() => {
        api.get("/household/checkin", { params: { month } })
           .then(r => setData(r.data)).catch(() => setData(null));
    }, [month]);

    return (
        <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="couple-checkin-page">
            <HouseholdNav />
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                    <HeartHandshake size={12} /> Couple VAULT Check-In
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                    {data?.headline || "Your financial month, together."}
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Five minutes, once a month. Composed from records currently in VAULT — never
                    judgemental, never comparative between members.
                </p>
                <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                       className="mt-3 border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                       data-testid="checkin-month" />
            </div>

            {data && (
                <>
                    <section className="bg-white border border-stone-200 rounded-2xl p-5 mb-4" data-testid="checkin-changes">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">What changed</div>
                        {(data.changes || []).length === 0 ? (
                            <div className="text-[13px] text-stone-500">No notable movements versus last month.</div>
                        ) : (
                            <ul className="space-y-2">
                                {data.changes.map(c => (
                                    <li key={c.category} className="flex items-center gap-2 text-[13px] text-stone-800">
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                        <span>{c.line}</span>
                                        <span className="ml-auto text-[11.5px] text-stone-500 tabular-nums">
                                            {fmtINR(c.previous)} → {fmtINR(c.current)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                        {[
                            { label: "Upcoming bills · 30d",     value: data.upcoming?.bills,       to: "/bills",         key: "bills" },
                            { label: "Upcoming renewals · 30d",  value: data.upcoming?.renewals,    to: "/renewals",      key: "renewals" },
                            { label: "Shared subscriptions",     value: data.shared_subscriptions, to: "/subscriptions", key: "subs" },
                            { label: "Joint expenses needing review", value: data.joint_needs_review, to: "/expenses",   key: "joint" },
                            { label: "Documents expiring · 90d", value: data.documents_expiring,   to: "/documents",     key: "docs" },
                        ].map(m => (
                            <Link key={m.key} to={m.to}
                                  className="rounded-xl border border-stone-200 bg-white p-3.5 hover:border-stone-500 transition-colors"
                                  data-testid={`checkin-${m.key}`}>
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{m.label}</div>
                                <div className="text-2xl font-display tabular-nums text-stone-900 mt-1">{m.value ?? 0}</div>
                            </Link>
                        ))}
                    </div>

                    {(data.shared_goals || []).length > 0 && (
                        <section className="bg-white border border-stone-200 rounded-2xl p-5 mb-4" data-testid="checkin-goals">
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Shared goals</div>
                            <ul className="space-y-1.5">
                                {data.shared_goals.map(g => (
                                    <li key={g.id} className="flex items-center gap-2 text-[13px] text-stone-800">
                                        <span>{g.title}</span>
                                        <span className="text-[10.5px] uppercase tracking-widest text-amber-800 font-semibold ml-2">{g.kind}</span>
                                        <span className="ml-auto text-[11.5px] text-stone-500">
                                            {g.target_amount ? fmtINR(g.target_amount) : "no target"}
                                            {g.target_date && ` · ${g.target_date}`}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/household/goals" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-amber-800">
                                Open goals <ArrowRight size={11} />
                            </Link>
                        </section>
                    )}
                    <div className="text-[11px] text-stone-500 max-w-2xl">{data.note}</div>
                </>
            )}
        </div>
    );
}
